import { API, getToken } from "@/lib/api";

/** BuiltBuddy — shared meta + URL helpers (mirrors backend/builtbuddy.py). */

export const DISCIPLINE_META = {
  architectural: { label: "Architectural", short: "ARCH", cls: "border-brand-steel/40 bg-brand-steel/10 text-brand-steel-deep dark:text-foreground" },
  structural: { label: "Structural", short: "STR", cls: "border-brand-navy/30 bg-brand-navy/10 text-brand-navy dark:text-foreground" },
  civil: { label: "Civil", short: "CIV", cls: "border-[hsl(var(--success)/0.4)] bg-[hsl(var(--success)/0.1)] text-[hsl(var(--success))]" },
  mep: { label: "MEP / Services", short: "MEP", cls: "border-[hsl(var(--info)/0.45)] bg-[hsl(var(--info)/0.1)] text-[hsl(var(--info))]" },
  shop: { label: "Shop drawings", short: "SHOP", cls: "border-[hsl(var(--warning)/0.45)] bg-[hsl(var(--warning)/0.1)] text-[hsl(var(--warning))]" },
  cad: { label: "CAD models", short: "CAD", cls: "border-primary/40 bg-primary/10 text-primary" },
  other: { label: "Other documents", short: "DOC", cls: "border-border bg-muted/40 text-muted-foreground" },
};
export const disciplineMeta = (key) => DISCIPLINE_META[key] || DISCIPLINE_META.other;

/** Severity scale — red is reserved for clashes / discrepancies (design addendum). 107C: near_match is amber (warning token) — same
 *  thing worded more specifically on one side; never a discrepancy. 107: accepted rows are blue (info token). */
export const SEVERITY_META = {
  critical: { label: "CRITICAL", cls: "border-[hsl(var(--destructive)/0.35)] bg-[hsl(var(--destructive)/0.12)] text-[hsl(var(--destructive))]" },
  major: { label: "MAJOR", cls: "border-[hsl(var(--destructive)/0.25)] bg-[hsl(var(--destructive)/0.08)] text-foreground" },
  minor: { label: "MINOR", cls: "border-border bg-muted/30 text-muted-foreground" },
  near_match: { label: "NEAR MATCH", cls: "border-[hsl(var(--warning)/0.45)] bg-[hsl(var(--warning)/0.12)] text-[hsl(var(--warning))]" },
  info: { label: "INFO", cls: "border-[hsl(var(--info)/0.25)] bg-[hsl(var(--info)/0.1)] text-foreground" },
};
export const severityMeta = (key) => SEVERITY_META[key] || SEVERITY_META.info;
/** 107 — review-state colours shared by the schedule table, discrepancy table and totals. */
export const ACCEPTED_CLS = "border-[hsl(var(--info)/0.45)] bg-[hsl(var(--info)/0.12)] text-[hsl(var(--info))]";
export const NEAR_MATCH_TEXT = "Near Match — review recommended";
/** "Accepted by Sam on 13 Oct, 14:34" — what a review stamp reads (accepted / flagged views from the API). */
export const reviewStamp = (verb, view) => {
  if (!view) return "";
  const d = new Date(view.at || "");
  const when = Number.isNaN(d.getTime()) ? "" : d.toLocaleString("en-AU", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" });
  return `${verb} by ${view.by || "the team"}${when ? ` on ${when}` : ""}`;
};

const tok = () => encodeURIComponent(getToken() || "");

/** Full page render (JPEG) of one document page. */
export const bbPageImageUrl = (jobId, fileId, page) => `${API}/builtbuddy/${jobId}/page-image?file_id=${fileId}&page=${page}&token=${tok()}`;

/** Evidence crop — the expanded window around the cited region. */
export const bbCropUrl = (jobId, ev) => {
  const c = ev.crop_bbox || [0, 0, 1, 1];
  return `${API}/builtbuddy/${jobId}/crop?file_id=${ev.file_id}&page=${ev.page}&x0=${c[0]}&y0=${c[1]}&x1=${c[2]}&y1=${c[3]}&token=${tok()}`;
};

/** Marked-up sheet (JPEG) for a clash analysis — red circles + numbered pins drawn server-side (exportable). */
export const bbClashMarkupUrl = (jobId, analysisId, fileId, page) =>
  `${API}/builtbuddy/${jobId}/clash/${analysisId}/markup?file_id=${fileId}&page=${page}&token=${tok()}`;

/** Marked-up sheet (JPEG) for a vertical-stacking analysis — stack pins (red when blocked, amber when clear) + bulkhead pins. */
export const bbStacksMarkupUrl = (jobId, analysisId, fileId, page) =>
  `${API}/builtbuddy/${jobId}/stacks/${analysisId}/markup?file_id=${fileId}&page=${page}&token=${tok()}`;

/** Marked-up sheet for either analysis kind. */
export const bbMarkupUrl = (jobId, analysis) =>
  analysis?.plan?.file_id ? (analysis.kind === "clash" ? bbClashMarkupUrl : bbStacksMarkupUrl)(jobId, analysis.id, analysis.plan.file_id, analysis.plan.page || 1) : null;

/** Crop of the MARKED-UP sheet around one pin (clash · stack · bulkhead) — the red circle is already drawn server-side. */
export const bbItemCropUrl = (jobId, analysisId, itemId, pad) =>
  `${API}/builtbuddy/${jobId}/analyses/${analysisId}/item-crop?item_id=${itemId}${pad != null ? `&pad=${pad}` : ""}&token=${tok()}`;

/** Branded Coordination Notes PDF for a clash / stacking analysis. */
export const bbCoordinationPdfUrl = (jobId, analysisId) => `${API}/builtbuddy/${jobId}/analyses/${analysisId}/coordination-pdf?token=${tok()}`;

/** Phase 183 — Comparison / Ask report PDF (instruction + BuiltBuddy's answer at the top, then the per-pair / per-page detail). */
export const bbReportPdfUrl = (jobId, analysisId) => `${API}/builtbuddy/${jobId}/analyses/${analysisId}/report.pdf?token=${tok()}`;

/** 101B — Window Schedule Comparison export (ext: "pdf" | "csv") for a finished Compare run. */
export const bbWindowTableUrl = (jobId, analysisId, ext = "pdf") => `${API}/builtbuddy/${jobId}/analyses/${analysisId}/window-table.${ext}?token=${tok()}`;
/** 102A — Window / Door Schedule Comparison export (kind: "windows" | "doors", ext: "pdf" | "csv"). */
export const bbScheduleTableUrl = (jobId, analysisId, kind = "windows", ext = "pdf") =>
  `${API}/builtbuddy/${jobId}/analyses/${analysisId}/schedule-table/${kind}.${ext}?token=${tok()}`;
/** Same test the server uses to switch a Compare run into window-schedule mode. */
export const WINDOW_FOCUS_RE = /\bwindow|\bglaz|\bw\d{1,3}\b|\bjoinery\b|\bfenestration\b/i;
/** …and into door-schedule mode (mirrors DOOR_FOCUS_RE in backend/builtbuddy.py). */
export const DOOR_FOCUS_RE = /\bdoors?\b|\bd\d{1,3}\b|\bfrl\b|\bleaf\b|\bhardware\b|\bcavity slider/i;
export const WINDOW_TOLERANCE_MM = 5;
export const SCHEDULE_TOLERANCE_MM = WINDOW_TOLERANCE_MM;

/** Schedule kinds a Compare run can check (mirrors bb_reports.SCHEDULE_KINDS) — what the table columns mean per kind. */
export const SCHEDULE_KIND_META = {
  windows: {
    label: "Window schedule", noun: "window", markWord: "Window mark", dims: ["Width", "Height"], dimCaption: "frame sizes", frameWord: "Frame W × H",
    // 107: shown as LEFT / RIGHT value columns (Glazing · Finish · Window type), not Yes/No match columns
    specs: [
      { field: "glazing", col: "Glazing" },
      { field: "finish", col: "Finish" },
      { field: "opening", col: "Window type" },
    ],
    subtextSpec: "opening",
    hint: `Window check on · ±${WINDOW_TOLERANCE_MM}mm frame sizes · glazing, finish + type · per-dwelling table, Accept / Flag, PDF/CSV, send to supplier`,
    tableTestId: "builtbuddy-window-table",
    rowPrefix: "builtbuddy-window",
    emptyHelp: "No window marks were read on the compared pages. Use Compare different pages to point at the window schedule and the matching shop drawing sheet.",
  },
  doors: {
    label: "Door schedule", noun: "door", markWord: "Door mark", dims: ["Leaf width", "Leaf height"], dimCaption: "leaf sizes", frameWord: "Leaf W × H",
    specs: [
      { field: "type", col: "Door type" },
      { field: "fire_rating", col: "Fire rating" },
      { field: "finish", col: "Finish / hardware" },
    ],
    subtextSpec: "",
    hint: `Door check on · ±${WINDOW_TOLERANCE_MM}mm leaf sizes · type, fire rating + finish/hardware · per-dwelling table, Accept / Flag, PDF/CSV, send to supplier`,
    tableTestId: "builtbuddy-door-table",
    rowPrefix: "builtbuddy-door",
    emptyHelp: "No door marks were read on the compared pages. Use Compare different pages to point at the door schedule and the matching joinery / shop drawing sheet.",
  },
};
export const scheduleKindMeta = (kind) => SCHEDULE_KIND_META[kind] || SCHEDULE_KIND_META.windows;
/** Schedule kinds a finished Compare run carries tables for (102A runs: schedule_modes · 101B runs: window_mode only). */
export const analysisScheduleKinds = (a) => {
  const kinds = Array.isArray(a?.schedule_modes) && a.schedule_modes.length ? a.schedule_modes : a?.window_mode ? ["windows"] : [];
  return kinds.filter((k) => SCHEDULE_KIND_META[k]);
};

/**
 * 111 — what each side of a comparison is called. The server detects 'Architectural' vs 'Shop Drawings' from the Plans
 * label / file name / document title (bb_reports.resolve_side_labels) and the user can force a side's role; these helpers
 * turn the server label into the short word printed in headers, table cells and badges.
 */
export const SIDE_ROLE_OPTIONS = [
  { key: "auto", label: "Auto" },
  { key: "architectural", label: "Arch" },
  { key: "shop", label: "Shop Drawings" },
  { key: "structural", label: "Structural" },
  { key: "document", label: "Document" },
];
const SIDE_PAREN = /\((left|right)\)/i;
/** "Architectural" → "Arch" · "Shop Drawings" → "Shop Drawings" · "Architectural (left)" → "Arch (left)". */
export const sideWord = (label = "", fallback = "") => {
  const m = String(label).match(SIDE_PAREN);
  const base = String(label).replace(/\s*\(.*?\)\s*/g, "").trim();
  const low = base.toLowerCase();
  const word = !base ? fallback : low.startsWith("arch") ? "Arch" : low.includes("shop") ? "Shop Drawings" : base;
  return `${word}${m ? ` (${m[1].toLowerCase()})` : ""}`;
};
/** "Architectural" → "ARCH" · "Shop Drawings" → "SHOP" · "Architectural (left)" → "ARCH L" — the 4-letter tag in table cells. */
export const sideTag = (label = "", fallback = "L") => {
  const m = String(label).match(SIDE_PAREN);
  const base = String(label).replace(/\s*\(.*?\)\s*/g, "").trim();
  const low = base.toLowerCase();
  const word = !base ? fallback : low.startsWith("arch") ? "ARCH" : low.includes("shop") ? "SHOP" : low.startsWith("struct") ? "STR" : base.slice(0, 4).toUpperCase();
  return `${word}${m ? ` ${m[1][0].toUpperCase()}` : ""}`;
};
/** Badge classes for a side label — steel for the architect's set, amber for the supplier's shop drawings. */
export const sideBadgeCls = (label = "") => {
  const low = String(label).toLowerCase();
  if (low.includes("shop")) return DISCIPLINE_META.shop.cls;
  if (low.startsWith("arch")) return DISCIPLINE_META.architectural.cls;
  if (low.startsWith("struct")) return DISCIPLINE_META.structural.cls;
  return DISCIPLINE_META.other.cls;
};
/** The two labels of a run (or a /compare/pairs proposal) — falls back to Left / Right. */
export const sideLabelsOf = (a) => {
  const l = Array.isArray(a?.side_labels) ? a.side_labels : [];
  return [l[0] || a?.left_label || "Left", l[1] || a?.right_label || "Right"];
};


/** Chat-card / PDF / RFI rows → PlanMarkupViewer items. */
export const analysisViewerItems = (a) =>
  (a?.items || []).map((it) => ({
    id: it.id, n: it.code, severity: it.severity, soft: !!it.soft, title: it.title, subtitle: it.subtitle || "", meta: it.meta || "",
    marker: it.marker, aria: `${it.code}, ${it.severity}, ${it.title}`, note: it.note, rfi: it.rfi || null, storey: it.storey || "",
  }));

/** RFI answer sync — an RFI raised from a pin is resolved once the consultant has answered or it was closed. */
export const rfiResolved = (rfi) => !!rfi && ["answered", "closed"].includes(rfi.status);

/**
 * Storey filter shared by the Stacks / Clash lists, the sheet pins and the bulk RFI: a stack matches on any storey it
 * runs through; bulkheads and clashes on their own storey. ``null`` / "" = all storeys.
 */
export const storeyMatches = (item, storey) => {
  if (!storey) return true;
  if (item.item_kind === "stack" || Array.isArray(item.present_storeys)) {
    return (item.present_storeys || []).includes(storey) || (item.crossings || []).some((c) => c.storey === storey) || (item.floors || []).some((f) => f.present && f.storey === storey);
  }
  return (item.storey || "") === storey;
};

/** Storey chip counts for a set of items (in the analysis' storey order). */
export const storeyCounts = (storeys, items) =>
  (storeys || []).map((s) => {
    const name = typeof s === "string" ? s : s?.name;
    return { name, count: (items || []).filter((it) => storeyMatches(it, name)).length };
  });

/** Compare tab — the storey a page pair belongs to: the saved level, else the level of either cited page (older runs). */
export const pairStorey = (p) =>
  p?.level || (p?.discrepancies || []).map((r) => r.left_evidence?.level || r.right_evidence?.level).find(Boolean) || "";

/** Compare tab — storeys present across the pairs (first-seen order) with their discrepancy-row counts. */
export const compareStoreys = (pairs) => {
  const seen = new Map();
  (pairs || []).forEach((p) => {
    const s = pairStorey(p);
    if (!s) return;
    seen.set(s, (seen.get(s) || 0) + (p.discrepancies || []).length);
  });
  return [...seen].map(([name, count]) => ({ name, count }));
};

/** 108 — client twin of bb_reports.unit_key: "Dwelling 2" → "dwelling2". */
export const unitKeyOfLabel = (label) => String(label || "").toLowerCase().replace(/[^a-z0-9]+/g, "");
const DWELLING_FAMILY_RE = /^(dwelling|unit|townhouse|villa|house|apartment)\s+\d/i;
const unitSortKey = (label) => {
  const m = /^(.*?)\s*(\d+)([A-Za-z]?)$/.exec(label || "");
  return m ? [m[1].toLowerCase(), Number(m[2]), m[3]] : [(label || "").toLowerCase(), 0, ""];
};
/**
 * 108 — the dwellings a Compare run covers, in site order: the schedule tables' unit groups first (they carry the
 * hydrated per-unit counts), then any dwelling a compared page pair was stamped with. Rows without a unit are not a tab;
 * "Lot 17" (the land) is dropped as soon as real dwelling labels exist — mirrors bb_reports.dwelling_labels.
 * → [{ key, label, open, near, total }]
 */
export const compareUnits = (analysis) => {
  const seen = new Map();
  analysisScheduleKinds(analysis).forEach((k) => {
    const wt = (analysis?.schedule_tables || {})[k] || (k === "windows" ? analysis?.window_table : null);
    (wt?.units || []).forEach((u) => {
      if (!u?.unit_key) return;
      const cur = seen.get(u.unit_key) || { key: u.unit_key, label: u.label || u.unit, open: 0, near: 0, total: 0 };
      cur.open += u.summary?.open || 0;
      cur.near += u.summary?.near || 0;
      cur.total += u.summary?.total || 0;
      seen.set(u.unit_key, cur);
    });
  });
  (analysis?.pairs || []).forEach((p) =>
    (p.units || []).forEach((u) => {
      const k = unitKeyOfLabel(u);
      if (k && !seen.has(k)) seen.set(k, { key: k, label: u, open: 0, near: 0, total: 0 });
    })
  );
  let units = [...seen.values()];
  if (units.some((u) => DWELLING_FAMILY_RE.test(u.label))) units = units.filter((u) => DWELLING_FAMILY_RE.test(u.label));
  return units.sort((a, b) => {
    const [aw, an, as] = unitSortKey(a.label);
    const [bw, bn, bs] = unitSortKey(b.label);
    return aw.localeCompare(bw) || an - bn || as.localeCompare(bs);
  });
};
/** 108 — does a compared page pair speak for this unit? Pairs whose sheets do not say which dwelling they cover show under every unit. */
export const pairInUnit = (p, unitKey) => !unitKey || !(p?.units || []).length || p.units.some((u) => unitKeyOfLabel(u) === unitKey);

/**
 * Coordination trend — run-over-run counts from the analyses history (`totals` per run, oldest → newest).
 * Only finished runs count. Returns null with fewer than two runs.
 * { points: [{id, at, critical, major, minor, total, storey, label}], first, last, delta, pct, peak }
 */
export const trendSeries = (analyses, kind = "clash") => {
  const runs = (analyses || [])
    .filter((a) => a && a.status === "done" && a.totals && (!kind || a.kind === kind))
    .slice()
    .sort((a, b) => String(a.created_at || "").localeCompare(String(b.created_at || "")));
  if (runs.length < 2) return null;
  const points = runs.map((a) => {
    const t = a.totals || {};
    const r = a.resolved || null; // from ?with_rfi=1 — pins already closed out through answered RFIs
    const critical = t.critical || 0;
    const major = t.major || 0;
    const minor = t.minor || 0;
    const total = kind === "stacks" ? (t.stacks || 0) + (t.bulkheads || 0) : t.clashes ?? critical + major + minor + (t.info || 0);
    const answered = Math.min(critical, r?.critical_answered || 0);
    return {
      id: a.id,
      at: a.created_at || "",
      critical,
      major,
      minor,
      total,
      answered, // critical pins with an answered / closed RFI
      openRfi: r?.critical_open || 0, // critical pins with an RFI still waiting
      answeredAll: r?.answered || 0,
      storey: a.storey || "",
      label: (a.models || []).map((m) => m.filename).join(" + ") || (kind === "stacks" ? "Stacking run" : "Clash run"),
    };
  });
  const first = points[0];
  const last = points[points.length - 1];
  const delta = last.critical - first.critical;
  const pct = first.critical ? Math.round((delta / first.critical) * 100) : null;
  const peak = Math.max(1, ...points.map((p) => p.critical + p.major));
  return { points, first, last, delta, pct, peak, answered: last.answered, remaining: Math.max(0, last.critical - last.answered) };
};

/** Deep link that lands on one pin: /jobs/{id}?bb=1&bb_tab=clash|stacks&bb_analysis=…&bb_item=… (mirrors bb_reports.pin_href). */
export const parseBbLink = (search) => {
  const q = new URLSearchParams(search || "");
  if (q.get("bb") !== "1") return null;
  const tab = q.get("bb_tab");
  return {
    open: true,
    mode: ["chat", "compare", "clash", "stacks"].includes(tab) ? tab : null,
    analysisId: q.get("bb_analysis") || null,
    itemId: q.get("bb_item") || null,
    voice: q.get("bb_voice") === "1", // hold-to-talk from the global bubble: open listening
  };
};

export const ANALYSIS_KIND_META = {
  clash: { label: "Exact clash check", tab: "Clash", noun: "clash" },
  stacks: { label: "Vertical mapping · stacks & bulkheads", tab: "Stacks", noun: "item" },
};

/** CAD element categories (mirrors backend/cad_parse.py CATEGORIES / STRUCTURAL / SERVICES). */
export const CAD_CATEGORY_META = {
  beam: { label: "Beams", one: "beam", group: "structure" },
  column: { label: "Columns", one: "column", group: "structure" },
  slab: { label: "Slabs", one: "slab", group: "structure" },
  wall: { label: "Walls", one: "wall", group: "structure" },
  footing: { label: "Footings", one: "footing", group: "structure" },
  stair: { label: "Stairs", one: "stair", group: "structure" },
  pipe: { label: "Pipes", one: "pipe", group: "services" },
  duct: { label: "Ducts", one: "duct", group: "services" },
  cable_tray: { label: "Cable trays", one: "cable tray", group: "services" },
  equipment: { label: "Equipment", one: "equipment", group: "services" },
  fixture: { label: "Fixtures", one: "fixture", group: "services" },
  opening: { label: "Openings", one: "opening", group: "other" },
  grid: { label: "Grids", one: "grid", group: "other" },
  other: { label: "Other", one: "element", group: "other" },
};
export const STRUCTURE_CATEGORIES = Object.keys(CAD_CATEGORY_META).filter((k) => CAD_CATEGORY_META[k].group === "structure");
export const SERVICE_CATEGORIES = Object.keys(CAD_CATEGORY_META).filter((k) => CAD_CATEGORY_META[k].group === "services");
export const cadCategoryMeta = (key) => CAD_CATEGORY_META[key] || CAD_CATEGORY_META.other;

/** "6,000" — millimetres with thousands separators, no decimals unless sub-mm matters. */
export const fmtMm = (v) => {
  const n = Number(v);
  if (!Number.isFinite(n)) return "–";
  return n.toLocaleString("en-AU", { maximumFractionDigits: Math.abs(n) < 10 ? 1 : 0 });
};

/** "X 6,000 · Y 4,000 · RL 2,750" for a clash point (RL omitted for plan-only models). */
export const fmtPoint = (pt, withZ = true) => {
  if (!pt) return "";
  const bits = [`X ${fmtMm(pt[0])}`, `Y ${fmtMm(pt[1])}`];
  if (withZ && pt.length > 2) bits.push(`RL ${fmtMm(pt[2])}`);
  return bits.join(" · ");
};

/** Registration key used by the backend: "{plan_file_id}:{page}". */
export const regKey = (fileId, page) => `${fileId}:${Math.max(1, Number(page) || 1)}`;

/** "1:50" style label for a CAD→sheet transform. */
export const fmtScale = (tf) => (tf?.scale_denominator ? `1:${tf.scale_denominator}` : tf?.scale ? `1:${Math.round(1 / tf.scale)}` : "");

/** Position of the cited bbox inside its crop window, as CSS percentages. */
export const highlightInCrop = (ev) => {
  const b = ev.bbox || [0, 0, 1, 1];
  const c = ev.crop_bbox || [0, 0, 1, 1];
  const w = Math.max(1e-6, c[2] - c[0]);
  const h = Math.max(1e-6, c[3] - c[1]);
  return {
    left: `${((b[0] - c[0]) / w) * 100}%`,
    top: `${((b[1] - c[1]) / h) * 100}%`,
    width: `${((b[2] - b[0]) / w) * 100}%`,
    height: `${((b[3] - b[1]) / h) * 100}%`,
  };
};

/** "S-03 · Rev A · p3/3" style citation. */
export const citation = (ev) => {
  const bits = [];
  if (ev.sheet_no) bits.push(ev.sheet_no);
  if (ev.revision) bits.push(`Rev ${ev.revision}`);
  bits.push(ev.page_count ? `p${ev.page}/${ev.page_count}` : `p${ev.page}`);
  return bits.join(" · ");
};

export const QUICK_ACTIONS = [
  { id: "footings", label: "Footing & slab summary", prompt: "Summarise the footing and slab design: sizes, concrete grade, reinforcement, and any notes the site team must not miss." },
  { id: "revisions", label: "What changed in the latest rev?", prompt: "Which sheets have more than one revision on file, and what changed in the latest revision compared with the earlier one?" },
  { id: "str-vs-arch", label: "Structural vs architectural", prompt: "Cross-check the structural drawings against the architectural drawings: do the footprint, grid, wall positions and levels agree? List any discrepancies." },
  { id: "services", label: "Services vs structure", prompt: "Check the services (plumbing, drainage, HVAC, electrical) against the structural slab, beams and footings — where could a service path be blocked or need a penetration, void or bulkhead?" },
  { id: "schedules", label: "Window & door schedule", prompt: "List the window and door schedule: marks, sizes and types, and flag any mark that appears on the plan but not in the schedule (or vice-versa)." },
];

/** Strip markdown for read-aloud. */
export const plainText = (md) =>
  String(md || "")
    .replace(/```[\s\S]*?```/g, " ")
    .replace(/[*_`#>|]/g, "")
    .replace(/\[(.*?)\]\(.*?\)/g, "$1")
    .replace(/\s+/g, " ")
    .trim();
